import { z } from 'zod';

// Default record-permission role keys for the ENTITY module. These match
// `record_permission_roles.key` values seeded by DEFAULT_RECORD_PERMISSION_ROLES.
// Custom per-org keys are also valid roleKey values — these constants exist for
// the UI's default role picker.
export const ENTITY_ROLE_KEY = {
  ENTITY_ADMIN: 'entity_admin',
  ACCOUNT_MANAGER: 'account_manager',
  ACCOUNT_CONTRIBUTOR: 'account_contributor',
  FINANCIAL_VIEWER: 'financial_viewer',
  READ_ONLY: 'read_only',
} as const;

export type EntityRoleKey = (typeof ENTITY_ROLE_KEY)[keyof typeof ENTITY_ROLE_KEY];

export enum EntityMemberStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
}

export const EntityMemberUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  emailVerified: z.date().nullable(),
  image: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
});

export const EntityMemberSchema = z.object({
  id: z.string().uuid(),
  entityId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  memberId: z.string().uuid().nullable().optional(),
  roleKey: z.string().min(1).max(100).default(ENTITY_ROLE_KEY.ACCOUNT_CONTRIBUTOR),
  status: z.nativeEnum(EntityMemberStatusEnum).default(EntityMemberStatusEnum.ACTIVE),
  settings: z.record(z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  user: EntityMemberUserSchema.nullable().optional(),
});

export type EntityMember = z.infer<typeof EntityMemberSchema>;

export const CreateEntityMemberSchema = EntityMemberSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  settings: true,
  user: true,
}).extend({
  settings: z.record(z.any()).optional(),
});

export type CreateEntityMember = z.infer<typeof CreateEntityMemberSchema>;

export const UpdateEntityMemberSchema = CreateEntityMemberSchema.partial();

export type UpdateEntityMember = z.infer<typeof UpdateEntityMemberSchema>;

export const EntityMembersResponseSchema = z.object({
  data: z.array(EntityMemberSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});

export type EntityMembersResponse = z.infer<typeof EntityMembersResponseSchema>;
